import { useState, useMemo } from "react";
import { View, TouchableOpacity } from "react-native";
import { Text, TouchableRipple, Searchbar } from "react-native-paper";
import { gql } from "@apollo/client";

import { Reviewer } from "../screens/types";
import Modal, { Props as ModalProps } from "./Modal";
import AvatarProfile from "./AvatarProfile";
import { colors } from "../../theme";
import TextInput, { Icon as TextInputIcon } from "./TextInput";
import ModalHeader from "./Modal/ModalHeader";
import splitMatch from "../utils/splitMatch";
import useSearchAndSelect from "../hooks/useSearchAndSelect";

interface SharedProps {
  description: string;
  onChange(reviewer?: Reviewer): void;
  placeholder?: string;
  testID?: string;
  title: string;
  value?: Reviewer;
}

export interface Props extends SharedProps {
  error?: { message?: string };
  onBlur(): void;
}

type MemberPickerProps = ModalProps & SharedProps;

export const QUERY = gql`
  query ($term: String!) {
    deferredCompReviewers(term: $term, limit: 10) {
      id
      name
      firstName
      lastName
      profilePhoto
      location
    }
  }
`;

const SuggestionItem = ({
  id,
  name,
  firstName,
  lastName,
  location,
  profilePhoto,
  searchText,
  onPressSuggestion,
}: Reviewer & { searchText: string; onPressSuggestion(id: string): void }) => {
  const segments = useMemo(() => splitMatch(name, searchText), [name, searchText]);

  return (
    <TouchableRipple onPress={() => onPressSuggestion(id)}>
      <View className="flex-row items-center py-3 px-12">
        <AvatarProfile
          {...{
            profilePhoto,
            firstName,
            lastName,
            size: 40,
          }}
        />
        <View className="flex-1 pl-3">
          <Text>
            {segments.map(({ text, isHighlighted }, i) => (
              <Text
                key={i}
                numberOfLines={1}
                className={`text-regular-tight ${isHighlighted ? "font-700" : "font-400"} text-ink-darkest`}
              >
                {text}
              </Text>
            ))}
          </Text>
          {!!location && <Text className="text-small-tight font-400 text-ink-lighter mt-2">{location}</Text>}
        </View>
      </View>
    </TouchableRipple>
  );
};

const ItemSeparatorComponent = () => <View className="border-b border-sky mx-6" />;

const renderItem =
  (searchText: string, onPressSuggestion: (reviewerId: string) => void) =>
  ({ item: { id, name, firstName, lastName, profilePhoto, location } }: { item: Reviewer }) => (
    <SuggestionItem
      key={id}
      {...{
        id,
        name,
        firstName,
        lastName,
        profilePhoto,
        searchText,
        location,
        onPressSuggestion,
      }}
    />
  );

export function MemberPicker({
  description,
  value,
  onChange,
  placeholder,
  testID,
  title,
  hasBorder,
  onDismiss,
  ...modalizeProps
}: MemberPickerProps) {
  const {
    searchText,
    onSearchTextChange,
    items: suggestionList,
    loading,
    showNoResultsFound,
  } = useSearchAndSelect<Reviewer>(QUERY);

  async function onPressSuggestion(reviewerId: string) {
    const reviewer = suggestionList.find((r) => r.id === reviewerId);
    if (reviewer) {
      onChange(reviewer);
    }
  }

  const HeaderComponent = (
    <>
      <ModalHeader {...{ title, hasBorder, onDismiss }} />
      <View className="p-6 bg-white">
        <Text className="text-small-normal mb-8 font-400 text-ink-dark">{description}</Text>
        <Searchbar
          autoFocus
          elevation={0}
          placeholderTextColor={colors.ink.light}
          style={{
            backgroundColor: "white",
            borderColor: colors.sky.dark,
            borderRadius: 8,
            borderWidth: 1,
          }}
          {...{ loading, testID, onChangeText: onSearchTextChange, placeholder }}
          value={searchText}
        />
        {showNoResultsFound && !!searchText && (
          <View className="justify-center items-center mt-4">
            <Text className="text-ink-light text-regular-tight">No results found.</Text>
          </View>
        )}
      </View>
    </>
  );

  return (
    <Modal
      fullScreen
      webStyle={{ height: "75vh" }}
      flatListProps={{
        ItemSeparatorComponent,
        renderItem: renderItem(searchText, onPressSuggestion),
        data: suggestionList,
        extraData: searchText,
      }}
      {...{
        HeaderComponent,
        ...modalizeProps,
        ...{ title, hasBorder, onDismiss },
      }}
    />
  );
}

export default function MemberPickerInput({ onChange, value, title, description, testID, placeholder, ...rest }: Props) {
  const [showModal, setShowModal] = useState(false);

  function onPress() {
    setShowModal(true);
  }

  function hideModal() {
    setShowModal(false);
  }

  return (
    <TouchableOpacity activeOpacity={0.7} {...{ testID, onPress }}>
      <TextInput
        editable={false}
        pointerEvents="none"
        left={<TextInputIcon icon="user" onPress={onPress} />}
        value={value ? value.name : ""}
        placeholder={placeholder}
        {...rest}
      />

      <MemberPicker
        onDismiss={hideModal}
        visible={showModal}
        testID="member-picker-input"
        onChange={(reviewer) => {
          onChange(reviewer);
          if (reviewer) hideModal();
        }}
        {...{ title, description, placeholder, value }}
      />
    </TouchableOpacity>
  );
}
